import React, { useEffect, useMemo, useState } from 'react';
import { ErrorMessage, Formik } from 'formik';
import { CSSTransition } from 'react-transition-group';
import pluralize from 'pluralize';

import LoadMask from 'components/LoadMask';
import BasicLabel from 'components/BasicLabel';
import BasicSelect from 'components/BasicSelect';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicSubmitButton from 'components/BasicSubmitButton';
import HorizontalLoader from 'components/HorizontalLoader';

import { SetupEmailReassignFormValidationSchema } from 'scenes/Form/components/SetupEmailReassignForm/validate';
import { useApi } from 'hooks/useApi';
import { ENDPOINT } from 'constants/endpoints';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation';

import './styles.css';
import BasicButton from 'components/BasicButton';

const SetupEmailReassignForm = props => {
  const {
    actions: { proceed, cancel, handleError },
  } = props;

  const api = useApi();

  const [formData, setFormData] = useState({
    init: false,
    processes: [],
    options: {
      RecruiterID: [],
    },
  });

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.EMAIL_SETUP_JOB_REASSIGN,
      {},
      {
        handleError: response => cancel(response),
        handleSuccess: response => setFormData({ ...response, init: true }),
      }
    );
  }, []);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    api.post(
      ENDPOINT.FORM.SAVE.EMAIL_SETUP_JOB_REASSIGN,
      { recId: values.RecruiterID.value },
      {
        handleError: response => {
          setSubmitting(false);
          handleError(response, setErrors);
        },
        handleSuccess: () => proceed(),
      }
    );
  };

  const [jobs, projects] = useMemo(() => {
    const renderProcessList = (items, title) =>
      items.length > 0 ? (
        <div className="form__group form__group--compact">
          <strong>{pluralize(title, items.length)}:</strong>
          <ul className="re-assign-list">
            {items.map((item, i) => (
              <div className="re-assign-list--item" key={i}>
                {item.title}
              </div>
            ))}
          </ul>
        </div>
      ) : null;

    return [
      renderProcessList(
        formData.processes.filter(p => p.type === 'Job'),
        'Processes'
      ),
      renderProcessList(
        formData.processes.filter(p => p.type === 'Project'),
        'Projects'
      ),
    ];
  }, [formData.processes]);

  return formData.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={{ RecruiterID: null }}
          validationSchema={SetupEmailReassignFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content"
              style={{ maxWidth: '100%' }}
            >
              {isSubmitting && <LoadMask />}
              {jobs}
              {projects}
              <div className="form__group form__group--compact">
                <BasicLabel
                  name="RecruiterID"
                  required={false}
                  text="Recruiter"
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicSelect
                  name="RecruiterID"
                  value={values.RecruiterID}
                  isValid={
                    (!errors.RecruiterID && !!touched.RecruiterID) ||
                    !touched.RecruiterID
                  }
                  options={formData.options.RecruiterID}
                  required={true}
                  isClearable={true}
                  placeholder="Start Typing"
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />

                <ErrorMessage
                  name="RecruiterID"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup
                  groupStyles={{ width: '100%', justifyContent: 'left' }}
                >
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />
                  <BasicButton
                    controlFunc={() => cancel(null)}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    </div>
  );
};

export default SetupEmailReassignForm;
